// DS2.9 but with redux bindings
import React, { useEffect, useId } from 'react';
import UploadItemButtonDS, { UploadItemButtonProps } from '@wix/da-ds/pkg/UploadItemButton';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import { useTranslation } from 'react-i18next';
import { FileUploadOptions, useFileUpload } from '../utils/hooks/useFileUpload';
import { UploadFailureCallback, UploadSuccessCallback } from '../redux/uploads/types';

import s from './UploadItemButton.scss';

export interface Props extends Pick<
  UploadItemButtonProps,
  | 'className'
  | 'dropZoneClassName'
  | 'contentClassName'
  | 'titleClassName'
  | 'subtitleClassName'
  | 'title'
  | 'subtitle'
  | 'ctaLabel'
  | 'ctaVariant'
  | 'aboveCtaSlot'
  | 'belowCtaSlot'
> {
  uploadOptions: FileUploadOptions;
  customIcon?: React.ReactNode;
  disableIcon?: boolean;
  onDragChange?: (isDragging: boolean) => void;
  onSuccess?: UploadSuccessCallback;
  onFailure?: UploadFailureCallback;
}

const UploadItemButton: React.FC<Props> = ({
  uploadOptions,
  className,
  dropZoneClassName,
  contentClassName,
  titleClassName,
  subtitleClassName,
  onSuccess,
  onFailure,
  title,
  subtitle,
  customIcon,
  disableIcon,
  ctaLabel,
  ctaVariant,
  aboveCtaSlot,
  belowCtaSlot,
  onDragChange,
}) => {
  const { t } = useTranslation();
  const uniqueId = useId();
  const uploadId = uploadOptions.id || uniqueId;
  const { input, getDropzoneRootProps, isDragging, activeUploads } = useFileUpload({
    ...uploadOptions,
    id: uploadId,
    onSuccess,
    onFailure,
  });
  useEffect(() => {
    onDragChange && onDragChange(isDragging);
  }, [onDragChange, isDragging]);

  const iconType = customIcon ? 'custom' : disableIcon ? false : 'llama';

  const activeUpload = [...activeUploads].pop();
  const errorMessage = activeUpload?.error?.cause ? (
    <>
      {t('somethingWentWrong')}.{' '}
      <SimpleTooltip
        usePortal
        className={s['error-tooltip']}
        text={`${activeUpload?.error?.cause}`}
      >
        {t('common.learnMore')}
      </SimpleTooltip>
    </>
  ) : (
    activeUpload?.error?.message
  );

  return (
    <UploadItemButtonDS
      className={className}
      dropZoneClassName={dropZoneClassName}
      contentClassName={contentClassName}
      titleClassName={titleClassName}
      subtitleClassName={subtitleClassName}
      iconType={iconType}
      icon={customIcon}
      rootProps={getDropzoneRootProps()}
      title={title}
      subtitle={subtitle}
      ctaLabel={ctaLabel}
      ctaVariant={ctaVariant}
      dropReadyTitle={t('uploadZone.drop.title')}
      progressTitle={t('uploadZone.progress.title')}
      fileInput={input}
      isDropReady={isDragging}
      progress={activeUpload?.progress}
      errorMessage={errorMessage}
      aboveCtaSlot={aboveCtaSlot}
      belowCtaSlot={belowCtaSlot}
    />
  );
};
UploadItemButton.displayName = 'UploadItemButton';

export default UploadItemButton;
